import React, { Component, PropTypes } from 'react';
import { Field, FieldArray } from 'redux-form';
import partial from 'lodash/partial';
import truncate from 'lodash/truncate';
import classnames from 'classnames';
import {
  TextAreaField, RadioButtonField, SelectField, TextInputField
} from 'common/components/form_fields';
import isEmail from 'sane-email-validation';
import AnimatedButton from 'common/components/animated_button';
import ModalDialog from 'common/components/modal/modal_dialog';
import { WEIGHT_OPTIONS } from 'focus_groups/utils';

class ParticipantTable extends Component {
  constructor(props) {
    super(props);
    this.add = this.add.bind(this);
    this.remove = this.remove.bind(this);
    this.changeEmail = this.changeEmail.bind(this);
    this.state = { email: '', invalid: false };
  }

  changeEmail(e) {
    const email = e.target.value;
    const invalid = !isEmail(email);
    this.setState({ email, invalid });
  }

  add(e) {
    e.preventDefault();
    e.stopPropagation();

    const { email } = this.state;
    if (!isEmail(email)) {
      this.setState({ invalid: true });
      return;
    }

    this.props.fields.push({ email, weight: 2 });
    this.setState({ email: '', invalid: false });
  }

  remove(idx, e) {
    e.preventDefault();
    e.stopPropagation();
    this.props.fields.remove(idx);
  }

  renderEmail(props) {
    return (<span>
      <span className="hide-for-large">{ truncate(props.input.value, { length: 20 }) }</span>
      <span className="show-for-large">{ truncate(props.input.value, { length: 40 }) }</span>
    </span>);
  }

  render() {
    const { fields, isWeighted, creating } = this.props;
    const { email, invalid } = this.state;

    const groupClasses = classnames('form-group', { error: invalid });
    const controlClasses = classnames('form-control', { filled: email });

    return (<div>
      <h3>Participant Email</h3>

      { creating?
      <p>
        You will be able to add more participants anytime after you launch your focus group.
        We will not share this email with anyone. Promise.
      </p>:
      <p>You can add participants anytime. We will not share this email with anyone. Promise.</p> }

      <div className={groupClasses}>
        <input onChange={this.changeEmail} value={email} type="email" name="email"
          className={controlClasses} />
        <span className="bar" />
        <label htmlFor="email" className="form-label animate">Email</label>
        { invalid && <span className="error-msg">Please enter a valid email</span> }
      </div>
      <div>
        <button type="button" className="btn btn-md btn-secondary" onClick={this.add}>Add</button>
      </div>
      { !!fields.length && isWeighted && <div className="row participant-row">
        <div className="columns small-8 large-10" />
        <div className="columns small-4 small-offset-8 large-4 large-offset-0">
          <strong>Vote counts:</strong>
        </div>
      </div> }
      { fields.length ? fields.map((user, index) => (
        <div key={`new-participant-${user}`} className="row participant-row">
          <div className="small-8 large-10 columns">
            <Field
              name={`${user}.email`}
              component={this.renderEmail}
            />
          </div>
          <div className="small-6 large-4 columns weight-option">
            {isWeighted && <Field
              name={`${user}.weight`}
              component={SelectField}
              options={WEIGHT_OPTIONS}
            />}
          </div>
          <div className="small-2 columns">
            <button onClick={partial(this.remove, index)} className="fa fa-times-circle" />
          </div>
        </div>
      )) : null }
    </div>
   );
  }
}

ParticipantTable.propTypes = {
  fields: PropTypes.object,
  isWeighted: PropTypes.bool,
  creating: PropTypes.bool,
};

export default class Participants extends Component {
  constructor(props) {
    super(props);

    this.state = { showingMsgPreview: false };
    this.toggleMsgPreview = this.toggleMsgPreview.bind(this);
  }

  renderMsgPreview() {
    const { currentValues } = this.props;
    const { first_name, last_name, invite_message } = currentValues;
    const fullName = (first_name || last_name)? `${first_name} ${last_name}` : 'YOUR NAME';

    return (
      <ModalDialog modalOpen={this.state.showingMsgPreview}
          closeAction={this.toggleMsgPreview} modalHeader="Preview"
          dialogClasses="msg-preview-modal" onPrimary={this.toggleMsgPreview}
          primaryLabel="Done">
        <p>Hey there,</p>
        <br />
        <p>
          { fullName } needs your help. They invited you to give your opinion on
          the designs submitted in their project on crowdspring.<br/>
        </p>
        <br />
        <p>{ fullName } included the following note with the invitation:</p>
        <p>{ invite_message || 'YOUR PERSONAL MESSAGE WILL SHOW HERE' }</p>
        <br />
        <p>
          To accept the invitation and participate in the focus group (it takes just a few minutes),
          please click the link below:
        </p>
        <p>
          <a>https://crowdspring.com/focus-group-participate/56143/98531/0203e3e1-c8ea-472d-ac47-6126e709d7d1</a>
        </p>
        <br />
        <p>
          Since 2008, { fullName } and more than 60,000 of the world's best
          entrepreneurs, businesses and agencies have leveraged crowdspring to get affordable,
          high-quality custom logos, print design, product design, web design & naming for their
          businesses.
        </p>
        <p>See you soon,</p>
        <p>The crowdspring crew</p>
      </ModalDialog>
    );
  }

  toggleMsgPreview() {
    this.setState({ showingMsgPreview: !this.state.showingMsgPreview });
  }

  render() {
    const {
      handleSubmit, onCreate, toggleCreate, toggleUpdate, invalid, currentValues,
      isCreating, showAddParticipants, onUpdate, onBack, canWeightVotes
    } = this.props;
    const onSubmit = showAddParticipants ? onUpdate : onCreate;
    const toggle = showAddParticipants ? toggleUpdate : toggleCreate;

    return (
      <div className="invite-participants">
        <h2> { showAddParticipants? 'Add' : 'Invite' } Participants</h2>

        { !showAddParticipants &&
        <p>
          Feel free to invite family and friends, but the best feedback comes from colleagues
          in the industry and your customers. After you launch your focus group, we'll give you
          a link you can post on social networks or share with anyone you want.
        </p> }

        <form className="participant-form" onSubmit={handleSubmit(onSubmit)}>
          { canWeightVotes &&
          <fieldset>
            <h3>Weighted Votes</h3>
            <p>Do you want some participants&apos; votes count more than others?</p>
            <Field
              name="weight_votes"
              component={RadioButtonField}
              props={{ value: true }}
              label="Yes, use weighted votes."
            />
            <Field
              name="weight_votes"
              component={RadioButtonField}
              props={{ value: false }}
              label="No, all votes should be equal."
            />
          </fieldset> }

          { !showAddParticipants &&
          <fieldset>
            <h3>Your Name</h3>
            <p>Let the people you invite to your focus group know who you are.</p>
            <div className="row name-row">
              <div className="columns small-16 large-8">
                <Field name="first_name" label="First Name" component={TextInputField} />
              </div>
              <div className="columns small-16 large-8">
                <Field name="last_name" label="Last Name" component={TextInputField} />
              </div>
            </div>
          </fieldset> }

          <fieldset>
            <FieldArray
              name="users"
              component={ParticipantTable}
              props={{ isWeighted: currentValues.weight_votes, creating: !showAddParticipants }}
            />
          </fieldset>
          <fieldset>
            <h3>Personal Message</h3>
            <p>
              Tell participants why you need their help (i.e. "I'm working with crowdspring to
              design a logo for my new gift basket business and need your help picking the best
              design.") and how they can best help you.<br/>
              We'll include this message in the invitation email we send to each participant.
              You can <a onClick={this.toggleMsgPreview}>preview the invitation</a> to see
              what your invitees will receive.
            </p>
            <Field
              name="invite_message"
              component={TextAreaField}
              rows={5}
            />
          </fieldset>
          <div className="modal-actions">
            { !showAddParticipants &&
            <button className="btn btn-text btn-back" onClick={onBack}>
              <i className="fa fa-angle-left"></i> Back
            </button> }

            <AnimatedButton
              type="submit"
              className="btn btn-primary"
              processing={isCreating}
              disabled={invalid}
              label={!showAddParticipants ? "Launch Focus Group": "Add Participants"}
              processingLabel="Launching..."
            />

            <button className="btn btn-text btn-block" onClick={toggle}>Cancel</button>
          </div>
        </form>

        { this.renderMsgPreview() }
      </div>
    );
  }
};

Participants.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  toggleCreate: PropTypes.func.isRequired,
  toggleUpdate: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  showAddParticipants: PropTypes.bool.isRequired,
  currentValues: PropTypes.object,
  isCreating: PropTypes.bool,
  onBack: PropTypes.func,
  canWeightVotes: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./focus_groups/components/participants.jsx